/**
 * Pronta Entrega Selector — Web Component
 * Bloco de compra para produtos de pronta entrega (não-chinelo).
 * Gerencia seleção de variantes, validação de conflito e add-to-cart
 * com integração nativa ao tema (cart:update + cart drawer).
 */

if (!customElements.get('pronta-entrega-selector')) {
    class ProntaEntregaSelector extends HTMLElement {
        connectedCallback() {
            this.bindElements();
            this.bindEvents();
        }

        bindElements() {
            this.addToCartBtn = this.querySelector('[ref="addToCartBtn"]');
            this.addToCartLoading = this.querySelector('[ref="addToCartLoading"]');
            this.variantIdInput = this.querySelector('[ref="variantIdInput"]');
            this.conflictAlert = this.querySelector('[ref="conflictAlert"]');
            this.optionButtons = this.querySelectorAll('[data-option-value]');

            this.productId = this.dataset.productId;
            this.productTitle = this.dataset.productTitle;
            this.productHandle = this.dataset.productHandle;
            this.addToCartDefaultText = this.dataset.addToCartText || 'ADICIONAR AO CARRINHO';
        }

        bindEvents() {
            if (this.addToCartBtn) {
                this.addToCartBtn.addEventListener('click', () => this.handleAddToCart());
            }

            // Variant option selection
            this.optionButtons.forEach(btn => {
                btn.addEventListener('click', () => this.handleOptionClick(btn));
            });

            // "Ver carrinho" — event delegation on conflict alert
            if (this.conflictAlert) {
                this.conflictAlert.addEventListener('click', (e) => {
                    const link = e.target.closest('.lot-size-selector__conflict-link');
                    if (!link) return;
                    e.preventDefault();
                    const cartDrawer = document.querySelector('cart-drawer-component');
                    if (cartDrawer && typeof cartDrawer.open === 'function') {
                        cartDrawer.open();
                    }
                });
            }
        }

        handleOptionClick(btn) {
            // Deselect others in same option group
            const group = btn.closest('.pronta-entrega__option-group');
            if (group) {
                group.querySelectorAll('.lot-size-selector__size-btn').forEach(b => {
                    b.dataset.selected = 'false';
                    b.setAttribute('aria-pressed', 'false');
                });
            }
            btn.dataset.selected = 'true';
            btn.setAttribute('aria-pressed', 'true');

            this.updateVariant();
        }

        updateVariant() {
            const selectedOptions = [];
            const groups = this.querySelectorAll('.pronta-entrega__option-group');
            groups.forEach(group => {
                const selected = group.querySelector('.lot-size-selector__size-btn[data-selected="true"]');
                if (selected) {
                    selectedOptions.push(selected.dataset.optionValue);
                }
            });

            fetch(`/products/${this.productHandle}.js`, {
                headers: { 'Accept': 'application/json' }
            })
                .then(res => res.json())
                .then(product => {
                    const variant = product.variants.find(v => {
                        return selectedOptions.every((opt, i) => {
                            return v.options[i] === opt || v[`option${i + 1}`] === opt;
                        });
                    });

                    if (variant) {
                        if (this.variantIdInput) {
                            this.variantIdInput.value = variant.id;
                        }
                        if (this.addToCartBtn) {
                            this.addToCartBtn.disabled = !variant.available;
                            const btnText = this.addToCartBtn.querySelector('.lot-size-selector__add-btn-text');
                            if (btnText) {
                                btnText.textContent = variant.available
                                    ? this.addToCartDefaultText
                                    : 'ESGOTADO';
                            }
                        }
                    }
                })
                .catch(err => console.warn('[PRONTA-ENTREGA] Error fetching variants:', err));
        }

        /**
         * Determines the lot category of a product by title.
         * @param {string} title
         * @returns {'ready' | 'lot_special'}
         */
        getProductCategory(title) {
            // Lote 1 e 2 são os únicos com estoque — tudo é 'ready'
            return 'ready';
        }

        async handleAddToCart() {
            const variantId = this.variantIdInput?.value;
            if (!variantId) return;

            // ── Sem validação de conflito — lote 1 e 2 podem ser misturados livremente ──

            this.hideConflictAlert();

            // ── Disable button and show loading ──
            if (this.addToCartBtn) {
                this.addToCartBtn.disabled = true;
                this.addToCartBtn.dataset.adding = 'true';
            }
            if (this.addToCartLoading) {
                this.addToCartLoading.hidden = false;
            }

            // ── Get section IDs for section rendering (native theme integration) ──
            const cartItemsComponents = document.querySelectorAll('cart-items-component');
            const sectionIds = [];
            cartItemsComponents.forEach(component => {
                if (component.dataset?.sectionId) {
                    sectionIds.push(component.dataset.sectionId);
                }
            });

            try {
                const formData = new FormData();
                formData.append('id', variantId);
                formData.append('quantity', '1');
                if (sectionIds.length > 0) {
                    formData.append('sections', sectionIds.join(','));
                }

                const response = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
                    method: 'POST',
                    body: formData,
                    headers: {
                        'Accept': 'application/json'
                    }
                });

                const result = await response.json();

                if (result.status) {
                    // Error response from Shopify
                    console.error('Add to cart error:', result.message);
                    throw new Error(result.message || 'Failed to add to cart');
                }

                // ── Success: dispatch cart:update event (native theme integration) ──
                // This triggers the cart drawer to open and animations to play
                const cartUpdateEvent = new CustomEvent('cart:update', {
                    bubbles: true,
                    detail: {
                        resource: result,
                        sourceId: this.id,
                        data: {
                            source: 'pronta-entrega-selector',
                            itemCount: 1,
                            productId: this.productId,
                            sections: result.sections
                        }
                    }
                });
                document.dispatchEvent(cartUpdateEvent);

                // Show added state
                if (this.addToCartBtn) {
                    this.addToCartBtn.dataset.added = 'true';
                    setTimeout(() => {
                        if (this.addToCartBtn) {
                            this.addToCartBtn.removeAttribute('data-added');
                        }
                    }, 1500);
                }
            } catch (e) {
                console.error('[PRONTA-ENTREGA] Add to cart failed:', e);
                if (this.addToCartBtn) {
                    this.addToCartBtn.dataset.error = 'true';
                    const btnText = this.addToCartBtn.querySelector('.lot-size-selector__add-btn-text');
                    if (btnText) {
                        const originalText = btnText.textContent;
                        btnText.textContent = e.message || 'Erro ao adicionar';
                        setTimeout(() => {
                            if (btnText) btnText.textContent = originalText;
                            if (this.addToCartBtn) this.addToCartBtn.removeAttribute('data-error');
                        }, 3000);
                    }
                }
            } finally {
                if (this.addToCartBtn) {
                    this.addToCartBtn.disabled = false;
                    this.addToCartBtn.removeAttribute('data-adding');
                }
                if (this.addToCartLoading) {
                    this.addToCartLoading.hidden = true;
                }
            }
        }

        /**
         * Shows the conflict alert.
         * The "Ver carrinho" button is handled via event delegation in bindEvents().
         */
        showConflictAlert() {
            if (!this.conflictAlert) return;

            const textEl = this.conflictAlert.querySelector('[ref="conflictAlertText"]');
            if (textEl) {
                textEl.textContent =
                    'Seu carrinho já contém itens de lotes futuros. ' +
                    'Finalize o pedido atual ou remova os itens para adicionar produtos de pronta entrega.';
            }

            this.conflictAlert.removeAttribute('hidden');
            this.conflictAlert.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }

        /**
         * Hides the conflict alert.
         */
        hideConflictAlert() {
            if (this.conflictAlert) {
                this.conflictAlert.setAttribute('hidden', '');
            }
        }
    }

    customElements.define('pronta-entrega-selector', ProntaEntregaSelector);
}
